import React, { useState } from 'react';
import { Eye, EyeOff, Key, Loader2 } from 'lucide-react';
import { useToastContext } from '../../../contexts/ToastContext';
import { useChangePassword } from '../../../services/userService';
import PasswordStrengthIndicator from '../../shared/PasswordStrengthIndicator';
import { validatePasswordForm } from '../../../utils/passwordValidation';

const AdminPasswordSettings = () => {
  const toast = useToastContext();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  const mutation = useChangePassword({
    onSuccess: () => {
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setErrors({});
      toast.success('Password updated', 'Your password has been changed successfully.');
    },
    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to update password';
      const details = error?.response?.data?.errors;
      if (details?.current_password) {
        setErrors((prev) => ({ ...prev, currentPassword: details.current_password[0] }));
      }
      if (details?.new_password) {
        setErrors((prev) => ({ ...prev, newPassword: details.new_password[0] }));
      }
      toast.error('Password update failed', message);
    },
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSave = () => {
    const validation = validatePasswordForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    mutation.mutate({
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
      confirmPassword: formData.confirmPassword,
    });
  };

  const field = (id, label, value, onChange, show, setShow, error) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={id === 'currentPassword' ? 'current-password' : 'new-password'}
          className={`w-full px-3 py-2.5 pr-10 text-sm border bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 !rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/40 ${
            error ? 'border-red-300' : 'border-gray-200 dark:border-gray-600'
          }`}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );

  return (
    <div className="space-y-5 max-w-xl">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-50 mb-1">
          Change password
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Use a strong unique password for your admin account.
        </p>
      </div>

      <div className="space-y-4">
        {field(
          'currentPassword',
          'Current password',
          formData.currentPassword,
          (v) => handleChange('currentPassword', v),
          showCurrent,
          setShowCurrent,
          errors.currentPassword
        )}
        {field(
          'newPassword',
          'New password',
          formData.newPassword,
          (v) => handleChange('newPassword', v),
          showNew,
          setShowNew,
          errors.newPassword
        )}
        <PasswordStrengthIndicator password={formData.newPassword} />
        {field(
          'confirmPassword',
          'Confirm new password',
          formData.confirmPassword,
          (v) => handleChange('confirmPassword', v),
          showConfirm,
          setShowConfirm,
          errors.confirmPassword
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={mutation.isPending}
          className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2.5 !rounded-xl hover:bg-primary-700 text-sm font-medium disabled:opacity-50"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Key className="w-4 h-4" />
          )}
          Update password
        </button>
      </div>
    </div>
  );
};

export default AdminPasswordSettings;
